import { Box, Typography } from '@mui/material';
import { Identifier } from '@okletsplay/core';
import { ModalName } from 'app/enums';
import { modalsReducers } from 'app/store/modal/slice';
import { dispatchUnwrap } from 'app/utils/store';
import { withdraw } from 'modules/matches/store/current-match/repository';
import React, { FC, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { ActionGuard } from 'shared/components/ActionGuard';
import { useStyles } from './Withdraw.styles';
import { useEntityRepositoryStatus } from 'app/hooks/store';
import { EntityStoreName } from 'app/enums/store';
import { CurrentMatchStoreStatus } from 'modules/matches/store/current-match/enums';
import { LoaderButton } from 'shared/components/LoaderButton/LoaderButton';

interface WithdrawProps {
  matchId?: string;
  isAuthor: boolean;
}

export const Withdraw: FC<WithdrawProps> = ({ isAuthor, matchId }) => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { classes } = useStyles();
  const withdrawing = useEntityRepositoryStatus({
    [EntityStoreName.CurrentMatch]: CurrentMatchStoreStatus.Withdrawing,
  });
  const description = useMemo(
    () =>
      isAuthor
        ? 'Your team will be removed from the match, and the match will be cancelled.'
        : 'Your team will be removed from the match.',
    [isAuthor]
  );

  const withdrawHandler = useCallback(() => {
    if (!matchId) {
      return;
    }
    dispatchUnwrap(withdraw(matchId as Identifier), {
      success: () => {
        navigate(pathname);
        dispatch(modalsReducers.closeModal(ModalName.Match));
      },
    });
  }, [matchId, navigate, pathname, dispatch]);

  return (
    <Box className={classes.root}>
      <Typography className={classes.header}>
        Are you sure you want to withdraw from the match?
      </Typography>
      <Typography className={classes.description}>{description}</Typography>

      <ActionGuard>
        <LoaderButton
          size="medium"
          color="error"
          variant="outlined"
          loading={withdrawing}
          disabled={withdrawing}
          onClick={withdrawHandler}
          data-testid="withdraw-button"
        >
          Yes I&apos;m Sure
        </LoaderButton>
      </ActionGuard>
    </Box>
  );
};
